import { useMemo, useState } from "react";
import Pagination from "@/components/base/Pagination";
import { Skeleton } from "@/components/base/Loading";
import { usePagination } from "@/hooks/usePagination";
import { formatFullDate } from "@/lib/format";
import { editionLabel } from "@/lib/license";
import { codeStatusLabel, downloadCodeList, downloadCodeListAsText } from "@/lib/codePoolExport";
import BatchRedeemStats from "@/pages/admin/tools/components/BatchRedeemStats";
import type {
  CodeExportLog,
  GenerateCodesInput,
  LicenseCode,
  ProductOption,
} from "@/pages/admin/tools/hooks/useLicenseTools";

type StatusFilter = "all" | "unused" | "disabled" | "used";

const PAGE_SIZE = 10;

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "unused", label: "未使用" },
  { key: "disabled", label: "已停用" },
  { key: "used", label: "已使用" },
];

const DEFAULT_EDITIONS = [
  { key: "standard", label: "标准版" },
  { key: "professional", label: "专业版" },
  { key: "enterprise", label: "企业版" },
];

const STATUS_TONE: Record<string, string> = {
  unused: "bg-primary-100 text-primary-700",
  disabled: "bg-secondary-100 text-secondary-900",
  used: "bg-background-200 text-foreground-700",
};

interface CodePoolPanelProps {
  codes: LicenseCode[];
  exportLogs: CodeExportLog[];
  products: ProductOption[];
  loading: boolean;
  notify: (text: string, tone?: "success" | "error") => void;
  onGenerate: (input: GenerateCodesInput) => Promise<{ batchId: string; generated: number; codes: string[] }>;
  onLogExport: (batchId: string, count: number, kind?: string) => Promise<void>;
  onSetStatus: (ids: number[], status: "unused" | "disabled") => Promise<number>;
  onDelete: (ids: number[]) => Promise<number>;
}

function emptyForm(): GenerateCodesInput {
  return { productName: "", edition: "standard", seatCount: 1, maxActivations: 1, quantity: 10, note: "" };
}

export default function CodePoolPanel({ codes, exportLogs, products, loading, notify, onGenerate, onLogExport, onSetStatus, onDelete }: CodePoolPanelProps) {
  const [form, setForm] = useState<GenerateCodesInput>(emptyForm);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<{ batchId: string; codes: string[] } | null>(null);

  const [filter, setFilter] = useState<StatusFilter>("all");
  const [keyword, setKeyword] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);

  const editionOptions = useMemo(() => {
    const product = products.find((item) => item.name === form.productName);
    const list = product?.editions?.length ? product.editions : DEFAULT_EDITIONS;
    const map = new Map<string, string>();
    list.forEach((item) => map.set(item.key, item.label));
    DEFAULT_EDITIONS.forEach((item) => {
      if (!map.has(item.key)) map.set(item.key, item.label);
    });
    return Array.from(map, ([key, label]) => ({ key, label }));
  }, [products, form.productName]);

  const update = (patch: Partial<GenerateCodesInput>) => setForm((prev) => ({ ...prev, ...patch }));

  const stats = useMemo(() => {
    const total = codes.length;
    const unused = codes.filter((item) => item.status === "unused").length;
    const disabled = codes.filter((item) => item.status === "disabled").length;
    const used = codes.filter((item) => item.status === "used").length;
    return { total, unused, disabled, used };
  }, [codes]);

  const filtered = useMemo(() => {
    const term = keyword.trim().toLowerCase();
    return codes.filter((item) => {
      if (filter !== "all" && item.status !== filter) return false;
      if (!term) return true;
      return (
        item.code.toLowerCase().includes(term) ||
        (item.product_name ?? "").toLowerCase().includes(term) ||
        (item.batch_id ?? "").toLowerCase().includes(term)
      );
    });
  }, [codes, filter, keyword]);

  const { page, totalPages, total, pageSize, pagedItems, setPage } = usePagination(
    filtered,
    PAGE_SIZE,
    `${filter}|${keyword}`,
  );

  const allChecked = pagedItems.length > 0 && pagedItems.every((item) => selected.has(item.id));
  const selectedCodes = useMemo(() => codes.filter((item) => selected.has(item.id)), [codes, selected]);

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allChecked) pagedItems.forEach((item) => next.delete(item.id));
      else pagedItems.forEach((item) => next.add(item.id));
      return next;
    });
  };

  const submitGenerate = async () => {
    if (form.quantity < 1 || form.quantity > 500) {
      notify("单次生成数量需在 1 ~ 500 之间", "error");
      return;
    }
    setGenerating(true);
    try {
      const res = await onGenerate({ ...form, productName: form.productName.trim(), note: form.note.trim() });
      setResult({ batchId: res.batchId, codes: res.codes });
      notify(`已生成 ${res.generated} 个原始激活码（批次 ${res.batchId}）`, "success");
    } catch (err) {
      notify(err instanceof Error ? err.message : "生成失败", "error");
    } finally {
      setGenerating(false);
    }
  };

  const copyResult = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.codes.join("\n"));
      notify("已复制本批次全部激活码", "success");
    } catch {
      notify("复制失败，请手动选择复制", "error");
    }
  };

  const runStatus = async (status: "unused" | "disabled") => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const count = await onSetStatus(Array.from(selected), status);
      notify(status === "disabled" ? `已停用 ${count} 个激活码` : `已启用 ${count} 个激活码`, "success");
      setSelected(new Set());
    } catch (err) {
      notify(err instanceof Error ? err.message : "操作失败", "error");
    } finally {
      setBusy(false);
    }
  };

  const runDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`确定删除选中的 ${selected.size} 个原始激活码？该操作不可恢复。`)) return;
    setBusy(true);
    try {
      const count = await onDelete(Array.from(selected));
      notify(`已删除 ${count} 个激活码`, "success");
      setSelected(new Set());
    } catch (err) {
      notify(err instanceof Error ? err.message : "删除失败", "error");
    } finally {
      setBusy(false);
    }
  };

  const exportList = (scope: "all" | "selected" | "filtered", asText = false) => {
    const list = scope === "selected" ? selectedCodes : scope === "filtered" ? filtered : codes;
    if (list.length === 0) {
      notify("没有可导出的激活码", "error");
      return;
    }
    const label = scope === "selected" ? "所选" : scope === "filtered" ? "筛选结果" : "全部";
    if (asText) downloadCodeListAsText(list, label);
    else downloadCodeList(list, label);
    notify(`已导出 ${list.length} 个激活码（${label}）`, "success");
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: "激活码总数", value: stats.total, icon: "ri-key-2-line", tone: "primary" },
          { label: "未使用", value: stats.unused, icon: "ri-radar-line", tone: "primary" },
          { label: "已停用", value: stats.disabled, icon: "ri-forbid-2-line", tone: "secondary" },
          { label: "已使用", value: stats.used, icon: "ri-checkbox-circle-line", tone: "accent" },
        ].map((card) => {
          const toneClass: Record<string, string> = {
            primary: "bg-primary-100 text-primary-700",
            secondary: "bg-secondary-100 text-secondary-900",
            accent: "bg-accent-100 text-accent-700",
          };
          return (
            <div key={card.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs text-foreground-600">{card.label}</span>
                <span className={`flex h-8 w-8 items-center justify-center rounded-md ${toneClass[card.tone]}`}>
                  <i className={`${card.icon} text-base`} />
                </span>
              </div>
              <p className="mt-2 font-heading text-2xl font-semibold text-foreground-950">{card.value}</p>
            </div>
          );
        })}
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
            <i className="ri-magic-line" />
          </span>
          <div>
            <h2 className="font-heading text-base font-semibold text-foreground-950">批量生成原始激活码</h2>
            <p className="text-xs text-foreground-500">一次性生成一批裸码，导出后即可随安装包或邮件分发给客户。</p>
          </div>
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-foreground-700">适用产品</span>
            <input
              list="code-pool-products"
              value={form.productName}
              onChange={(event) => update({ productName: event.target.value })}
              placeholder="选择或输入产品名称"
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
            />
            <datalist id="code-pool-products">
              {products.map((item) => (
                <option key={item.name} value={item.name} />
              ))}
            </datalist>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-foreground-700">版本类型</span>
            <select
              value={form.edition}
              onChange={(event) => update({ edition: event.target.value })}
              className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
            >
              {editionOptions.map((item) => (
                <option key={item.key} value={item.key}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-foreground-700">生成数量（1 ~ 500）</span>
            <input
              type="number"
              min={1}
              max={500}
              value={form.quantity}
              onChange={(event) => update({ quantity: Number(event.target.value) || 0 })}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-foreground-700">授权席位</span>
            <input
              type="number"
              min={1}
              value={form.seatCount}
              onChange={(event) => update({ seatCount: Number(event.target.value) || 1 })}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-foreground-700">最大激活数</span>
            <input
              type="number"
              min={1}
              value={form.maxActivations}
              onChange={(event) => update({ maxActivations: Number(event.target.value) || 1 })}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-foreground-700">备注</span>
            <input
              type="text"
              value={form.note}
              onChange={(event) => update({ note: event.target.value })}
              placeholder="例如：双十一活动批次"
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
            />
          </label>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={generating}
            onClick={() => void submitGenerate()}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-add-circle-line" />
            </span>
            {generating ? "生成中…" : "生成激活码"}
          </button>
          <span className="text-[11px] text-foreground-500">生成的激活码为一次性裸码，请妥善保管并及时分发。</span>
        </div>

        {result && (
          <div className="mt-4 rounded-md border border-primary-200 bg-primary-50 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="flex items-center gap-2 text-sm font-medium text-foreground-950">
                <span className="flex h-4 w-4 items-center justify-center text-primary-700">
                  <i className="ri-checkbox-circle-line" />
                </span>
                本批次已生成 {result.codes.length} 个 · 批次号 {result.batchId}
              </p>
              <button
                type="button"
                onClick={() => void copyResult()}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100"
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-file-copy-line" />
                </span>
                复制本批次
              </button>
            </div>
            <p className="mt-2 max-h-24 overflow-y-auto rounded-md bg-background-50 p-2.5 font-mono text-[11px] leading-relaxed text-foreground-600">
              {result.codes.join("　")}
            </p>
          </div>
        )}
      </div>

      <BatchRedeemStats codes={codes} exportLogs={exportLogs} notify={notify} onLogExport={onLogExport} />

      <div className="rounded-lg border border-background-200 bg-background-50">
        <div className="flex flex-col gap-3 border-b border-background-200 p-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {STATUS_FILTERS.map((item) => {
              const active = item.key === filter;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setFilter(item.key)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                    active
                      ? "bg-primary-500 text-background-50"
                      : "border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
          <div className="relative w-full lg:max-w-xs">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-search-line" />
            </span>
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索激活码 / 产品 / 批次"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200 px-4 py-3">
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-foreground-800">
            <input
              type="checkbox"
              checked={allChecked}
              onChange={toggleAll}
              className="h-4 w-4 cursor-pointer accent-primary-600"
            />
            全选本页
            {selected.size > 0 && (
              <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-medium text-primary-700">
                已选 {selected.size}
              </span>
            )}
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy || selected.size === 0}
              onClick={() => void runStatus("disabled")}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <i className="ri-forbid-2-line" />
              停用
            </button>
            <button
              type="button"
              disabled={busy || selected.size === 0}
              onClick={() => void runStatus("unused")}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <i className="ri-refresh-line" />
              启用
            </button>
            <button
              type="button"
              disabled={busy || selected.size === 0}
              onClick={() => void runDelete()}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-accent-300 bg-accent-50 px-3 py-1.5 text-xs font-medium text-accent-800 transition-colors hover:bg-accent-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <i className="ri-delete-bin-line" />
              删除
            </button>
            <button
              type="button"
              onClick={() => exportList(selected.size > 0 ? "selected" : "filtered")}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100"
            >
              <i className="ri-download-2-line" />
              导出 {selected.size > 0 ? "所选" : "当前筛选"}
            </button>
            <button
              type="button"
              onClick={() => exportList("all")}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100"
            >
              <i className="ri-file-list-3-line" />
              导出全部
            </button>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3 p-4">
            {[0, 1, 2].map((item) => (
              <Skeleton key={item} className="h-12 w-full" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-14 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-md bg-background-200 text-foreground-500">
              <i className="ri-key-2-line text-2xl" />
            </span>
            <p className="text-sm font-medium text-foreground-800">没有匹配的激活码</p>
            <p className="max-w-sm text-xs text-foreground-500">调整筛选条件，或在上方批量生成一批新的激活码。</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="bg-background-100 text-left text-xs font-medium text-foreground-600">
                  <th className="w-12 px-4 py-3" />
                  <th className="px-4 py-3">激活码</th>
                  <th className="px-4 py-3">产品 / 版本</th>
                  <th className="px-4 py-3">席位 / 激活</th>
                  <th className="px-4 py-3">批次</th>
                  <th className="px-4 py-3">状态</th>
                  <th className="px-4 py-3">生成时间</th>
                </tr>
              </thead>
              <tbody>
                {pagedItems.map((item) => (
                  <tr key={item.id} className={`border-t border-background-200 ${selected.has(item.id) ? "bg-primary-50/60" : ""}`}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selected.has(item.id)}
                        onChange={() => toggleSelect(item.id)}
                        aria-label={`选择 ${item.code}`}
                        className="h-4 w-4 cursor-pointer accent-primary-600"
                      />
                    </td>
                    <td className="px-4 py-3 font-mono text-[13px] text-foreground-950">{item.code}</td>
                    <td className="px-4 py-3">
                      <p className="text-foreground-900">{item.product_name || "通用"}</p>
                      <p className="text-[11px] text-foreground-500">
                        {editionLabel(item.edition) || "不限"}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-foreground-700">
                      {item.seat_count} / {item.max_activations}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-foreground-500">{item.batch_id || "--"}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-1 text-[11px] ${STATUS_TONE[item.status] ?? "bg-background-200 text-foreground-700"}`}>
                        {codeStatusLabel(item.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-foreground-500">{formatFullDate(item.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="px-4 pb-4">
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onChange={setPage}
            itemLabel="个激活码"
          />
        </div>
      </div>
    </div>
  );
}